// Společné kousky B2B sekce (podle schválených mockupů v docs/mockupy/b2b).
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Building2, Landmark, HeartHandshake, GraduationCap, Dumbbell, Shapes, Copy, Check } from "lucide-react";
import { Pil } from "../sales/salesUi.jsx";
import { toast } from "../Toast.jsx";

export const STAVY = {
  k_osloveni: { label: "K oslovení", ton: "neutralni" },
  osloveno: { label: "Osloveno", ton: "info" },
  followup: { label: "Follow-up", ton: "pozor" },
  zajem: { label: "Zájem", ton: "ok" },
  schuzka: { label: "Schůzka", ton: "ok" },
  nabidka: { label: "Nabídka", ton: "znacka" },
  klient: { label: "Klient", ton: "zlata" },
  odlozeno: { label: "Odloženo", ton: "neutralni" },
  ztraceno: { label: "Ztraceno", ton: "neutralni" },
  nedoruceno: { label: "Nedoručeno", ton: "problem" },
  nekontaktovat: { label: "Nekontaktovat", ton: "problem" },
};
export const SEGMENTY = {
  firma: { label: "Firma", ikona: Building2 },
  verejna_sprava: { label: "Veřejná správa", ikona: Landmark },
  seniori: { label: "Senioři", ikona: HeartHandshake },
  skola: { label: "Škola", ikona: GraduationCap },
  fitness: { label: "Fitness", ikona: Dumbbell },
  jine: { label: "Jiné", ikona: Shapes },
};
export const ZDROJE = {
  reserse: "rešerše", poptavka_web: "poptávka z webu", doporuceni: "doporučení", byvaly_klient: "bývalý klient", import: "import",
};
export const VELIKOSTI = ["do 50", "50 až 250", "250+"];
export const TYPY_KROKU = { email: "E-mail", hovor: "Hovor", schuzka: "Schůzka", jine: "Jiné" };

export const volbyStavu = (vse = "Všechny") => [{ key: "", label: vse }, ...Object.entries(STAVY).map(([k, v]) => ({ key: k, label: v.label }))];
export const volbySegmentu = (vse = "Všechny") => [{ key: "", label: vse }, ...Object.entries(SEGMENTY).map(([k, v]) => ({ key: k, label: v.label }))];

export function StavPil({ stav, mala }) {
  const s = STAVY[stav] || { label: stav, ton: "neutralni" };
  return <Pil ton={s.ton} casti={s.label} mala={mala} />;
}
export function SegPil({ segment, mala }) {
  const s = SEGMENTY[segment] || SEGMENTY.jine;
  return <Pil ton="neutralni" ikona={s.ikona} casti={s.label} mala={mala} />;
}

// "2026-10-26" → "26. 10." a s rokem "26. 10. 2026".
export function dm(iso) { if (!iso) return ""; const [, m, d] = String(iso).substring(0, 10).split("-").map(Number); return `${d}. ${m}.`; }
export function dmr(iso) { if (!iso) return ""; const [y, m, d] = String(iso).substring(0, 10).split("-").map(Number); return `${d}. ${m}. ${y}`; }
export function dnesIsoPraha() { const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" })); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }

/** Text a tón data dalšího kroku: "dnes", "po termínu", jinak datum. */
export function krokPil(f) {
  if (!f.dalsi_krok_datum) return null;
  const dnes = dnesIsoPraha();
  const cas = f.dalsi_krok_cas ? ` ${f.dalsi_krok_cas}` : "";
  if (f.pozdni) return { ton: "problem", text: `${dm(f.dalsi_krok_datum)}, po termínu` };
  if (f.dalsi_krok_datum === dnes) return { ton: "pozor", text: `dnes${cas}` };
  return { ton: "neutralni", text: `${dm(f.dalsi_krok_datum)}${cas}` };
}

export function OtevritTlacitko({ id, nazev }) {
  const navigate = useNavigate();
  return (
    <button type="button" className="btn-ikona" aria-label={`Otevřít firmu ${nazev}`} title="Otevřít" onClick={() => navigate(`/b2b/firma/${id}`)}>
      <ArrowUpRight className="h-5 w-5" />
    </button>
  );
}

async function zkopiruj(text, co) {
  try {
    await navigator.clipboard.writeText(text);
    toast(`Zkopírováno: ${co}.`, "success");
  } catch {
    toast("Kopírování se nepovedlo, označ text ručně.", "error");
  }
}

/** Řádek kontaktu: odkaz (telefon, e-mail, web) a vedle něj tlačítko Kopírovat. */
export function KontaktRadek({ ikona: Ikona, href, hodnota, kopie, externi = false }) {
  const [hotovo, setHotovo] = useState(false);
  return (
    <div className="flex min-h-[44px] items-center gap-2">
      <Ikona className="h-4 w-4 shrink-0 text-slate-400" />
      <a href={href} {...(externi ? { target: "_blank", rel: "noreferrer" } : {})} className="min-w-0 flex-1 text-sm font-semibold text-brand-700 underline decoration-brand-200 underline-offset-2 [overflow-wrap:anywhere]">{hodnota}</a>
      <button type="button" className="btn-ikona" aria-label={`Kopírovat ${kopie}`} title={`Kopírovat ${kopie}`}
        onClick={async () => { await zkopiruj(hodnota, kopie); setHotovo(true); setTimeout(() => setHotovo(false), 1500); }}>
        {hotovo ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

/** Web jako odkaz: doplní https://, když chybí. */
export function webHref(w) { const t = String(w || "").trim(); return /^https?:\/\//i.test(t) ? t : `https://${t}`; }
export function telHref(t) { return `tel:${String(t || "").replace(/[^\d+]/g, "")}`; }

/* --- Akce a ceník (fáze 3a) ------------------------------------------------ */
export const STAVY_AKCE = {
  nabidka: { label: "Nabídka", ton: "znacka" },
  potvrzeno: { label: "Potvrzeno", ton: "ok" },
  realizovano: { label: "Realizováno", ton: "info" },
  vyfakturovano: { label: "Vyfakturováno", ton: "pozor" },
  uhrazeno: { label: "Uhrazeno", ton: "zlata" },
  zruseno: { label: "Zrušeno", ton: "neutralni" },
};
export const JEDNOTKY = { akce: "za akci", osoba: "za osobu", den: "za den" };
export const volbyStavuAkce = (vse = "Všechny") => [{ key: "", label: vse }, ...Object.entries(STAVY_AKCE).map(([k, v]) => ({ key: k, label: v.label }))];

export function StavAkcePil({ stav, mala }) {
  const s = STAVY_AKCE[stav] || { label: stav, ton: "neutralni" };
  return <Pil ton={s.ton} casti={s.label} mala={mala} />;
}
/** 12000 → "12 000 Kč" (pevné mezery, ať se částka nezalomí). */
export function kc(n) {
  if (n === null || n === undefined || n === "") return "bez ceny";
  return `${Math.round(Number(n)).toLocaleString("cs-CZ").replace(/\s/g, " ")} Kč`;
}
export function cenaText(p) {
  if (p.cena_od == null && p.cena_do == null) return "cena zatím není";
  if (p.cena_od == null || p.cena_od === p.cena_do) return kc(p.cena_od ?? p.cena_do);
  if (p.cena_do == null) return `od ${kc(p.cena_od)}`;
  return `${kc(p.cena_od)} až ${kc(p.cena_do)}`;
}
export function terminText(a) {
  const cas = a.cas_od ? (a.cas_do ? `${a.cas_od} až ${a.cas_do}` : a.cas_od) : "";
  return { datum: a.datum ? dmr(a.datum) : "termín neurčen", cas };
}
export function marzePil(a, prah = 30, mala = true) {
  if (a.stav === "zruseno" || a.marze_pct == null) return null;
  return <Pil ton={a.marze_pct < prah ? "pozor" : "ok"} mala={mala} casti={["marže", `${String(a.marze_pct).replace(".", ",")} %`]} />;
}
